import {
  Braces,
  Building2,
  Database,
  FileText,
  Globe,
  History,
  House,
  Image,
  Inbox,
  LayoutTemplate,
  Menu,
  ShoppingBag,
  Star,
  UserRound,
  Zap,
} from 'lucide-react';
import type { ReactNode } from 'react';

import { t } from '@/shared/i18n';

import type { EnvKey } from '../site/scope';
import type { SiteScope } from '../site/useSiteScope';

/*
 * Чистые правила навигации — отдельно от компонентов: их проверяют тесты, а
 * файлы компонентов по правилу быстрой перезагрузки экспортируют только
 * компоненты.
 */

export interface NavEntry {
  href: string;
  icon: ReactNode;
  label: string;
  /** Пункт активен и на вложенных адресах. */
  prefix?: boolean;
  /** Счётчик у пункта — например, новые заявки форм. */
  badge?: number;
}

export interface NavGroup {
  /** Подпись группы; у первой группы её нет. */
  title?: string;
  /**
   * Прижать вниз колонки, к меню человека: служебные разделы (настройки,
   * журнал) нужны реже содержимого и не должны теснить его наверху.
   */
  bottom?: boolean;
  /**
   * Ключ сворачиваемого раздела (сайт и раздел): разделы содержимого
   * сворачиваются, и у длинного видны первые пункты и «Ещё N». Иначе при
   * пятнадцати коллекциях колонка становится простынёй.
   */
  foldKey?: string;
  entries: NavEntry[];
}

/** Сколько пунктов раздела видно, пока его не раскрыли целиком. */
export const SECTION_LIMIT = 7;

/**
 * Какие пункты раздела показать.
 *
 * Свёрнутый раздел показывает только открытый сейчас пункт — чтобы не
 * терять, где находишься. Длинный — первые SECTION_LIMIT и открытый, если он
 * дальше; остальное — за «Ещё N».
 */
export function visibleEntries(
  entries: NavEntry[],
  options: { folded: boolean; expanded: boolean; isActive: (entry: NavEntry) => boolean },
): { shown: NavEntry[]; hidden: number } {
  if (options.folded) {
    return { shown: entries.filter(options.isActive), hidden: 0 };
  }

  if (options.expanded || entries.length <= SECTION_LIMIT + 1) {
    return { shown: entries, hidden: 0 };
  }

  const head = entries.slice(0, SECTION_LIMIT);
  const active = entries.slice(SECTION_LIMIT).filter(options.isActive);

  return { shown: [...head, ...active], hidden: entries.length - head.length - active.length };
}

/**
 * Разделы колонки.
 *
 * Вне сайта — сайты и (владельцу платформы) тенанты. Внутри сайта меню
 * строится из его схемы: у магазина «Товары» и «Заказы», у лендинга —
 * «Страницы». Каждый пункт виден только тем, у кого есть на него право:
 * недоступное не висит серым, а не показывается вовсе
 * (docs/research/07-navigation-and-home.md, матрица ролей —
 * docs/research/03-roles.md).
 */
export function navFor(
  scope: SiteScope | null,
  superAdmin: boolean,
  favorites: readonly string[] = [],
): NavGroup[] {
  if (scope === null) {
    return [
      {
        entries: [
          { href: '/sites', icon: <Globe />, label: t.nav.sites },
          ...(superAdmin ? [{ href: '/tenants', icon: <Building2 />, label: t.nav.tenants }] : []),
        ],
      },
      ...(superAdmin
        ? [
            {
              bottom: true,
              entries: [{ href: '/activity', icon: <History />, label: t.nav.activity }],
            },
          ]
        : []),
    ];
  }

  const base = `/sites/${scope.siteId}/${scope.env}`;
  const reader = scope.can('content.read');
  // Возможности сайта (ресерч 13): чего у сайта нет, того нет и в меню —
  // включают в «Настройках → Возможности».
  const has = (feature: 'users' | 'shop' | 'forms') =>
    scope.site?.features?.includes(feature) === true;
  const forms = scope.types.filter(
    (type) => type.purpose === 'form' && type.id !== scope.shop?.orders?.id,
  );

  // Разделы — постоянные, а не список моделей: моделей на большом сайте
  // десятки (ресерч 10, замечание Давида). Настройки, команда и журнал — в
  // меню сайта (SiteSwitcher), перенос — на «Моделях» и «Компонентах».
  const main: NavEntry[] = [{ href: base, icon: <House />, label: t.nav.home }];

  if (reader) {
    main.push(
      { href: `${base}/pages`, icon: <FileText />, label: t.nav.pages, prefix: true },
      { href: `${base}/models`, icon: <Database />, label: t.nav.models, prefix: true },
    );

    // Форму могли завести и вручную в «Моделях» — тогда раздел виден и без
    // галочки, иначе заявки негде читать. Заказы магазина — тоже форма, но
    // живут в «Магазине».
    if (has('forms') || forms.length > 0) {
      main.push({
        href: `${base}/forms`,
        icon: <Inbox />,
        label: t.nav.forms,
        prefix: true,
        badge: forms.reduce((sum, type) => sum + type.unread_count, 0),
      });
    }

    if (has('users')) {
      main.push({
        href: `${base}/users`,
        icon: <UserRound />,
        label: t.nav.users,
        // Новые регистрации — так же, как новые заявки.
        badge: scope.types
          .filter((type) => type.purpose === 'users')
          .reduce((sum, type) => sum + type.unread_count, 0),
      });
    }
  }

  if (reader && has('shop')) {
    main.push({
      href: `${base}/shop`,
      icon: <ShoppingBag />,
      label: t.nav.shop,
      prefix: true,
      badge: scope.types.find((type) => type.id === scope.shop?.orders?.id)?.unread_count ?? 0,
    });
  }

  if (scope.can('site.shape')) {
    main.push(
      {
        href: `${base}/components`,
        icon: <LayoutTemplate />,
        label: t.nav.components,
        prefix: true,
      },
      { href: `${base}/automations`, icon: <Zap />, label: t.nav.automations, prefix: true },
    );
  }

  if (reader) {
    main.push({ href: `${base}/navigation`, icon: <Menu />, label: t.nav.navigation });
  }

  if (reader) {
    main.push(
      { href: `${base}/constants`, icon: <Braces />, label: t.nav.constants, prefix: true },
      { href: `${base}/media`, icon: <Image />, label: t.nav.media },
    );
  }

  const groups: NavGroup[] = [{ entries: main }];

  // Избранное — модели, отмеченные звёздочкой, в порядке отметки.
  const starred = reader
    ? favorites
        .map((slug) => scope.types.find((type) => type.slug === slug && type.kind !== 'block'))
        .filter((type): type is (typeof scope.types)[number] => type !== undefined)
    : [];

  if (starred.length > 0) {
    groups.push({
      title: t.nav.favorites,
      foldKey: `${scope.siteId}:favorites`,
      entries: starred.map((type) => ({
        href:
          type.purpose === 'users'
            ? `${base}/users`
            : `${base}/${type.purpose === 'form' ? 'forms' : 'models'}/${type.id}`,
        icon: <Star />,
        label: type.name,
        prefix: true,
      })),
    });
  }

  return groups;
}

/**
 * Адрес того же раздела в другом окружении. Кроме записи: в другом
 * окружении это другая запись, поэтому переход ведёт на главную сайта.
 */
export function targetPath(pathname: string, siteId: string, from: EnvKey, to: EnvKey): string {
  const prefix = `/sites/${siteId}/${from}`;
  const rest = pathname.startsWith(prefix) ? pathname.slice(prefix.length) : '';

  if (rest.startsWith('/r/')) {
    return `/sites/${siteId}/${to}`;
  }

  // Деталка модели, компонента или константы — в другом окружении у них
  // другой id: ведём в сам раздел.
  const section = /^\/(models|forms|components|constants)\/[^/]+/.exec(rest);
  const keep = section === null ? rest : `/${section[1] ?? ''}`;

  return `/sites/${siteId}/${to}${keep}`;
}
